import { getBoqVersionRoutePrefix } from '../utils/boqVersionPrefix';
import { useEffect, useMemo } from "react";
import { useTender } from "../contexts/TenderContext";
import { sumPrelimsAmount } from "../utils/prelimsMapping";
import type { ItemLibraryItem, ItemLibraryResponse } from "../api/itemLibrary";
import type { PumpChamberConfig, DynamicBillSummaryNode } from "../types/tender.types";
import { buildFoulSummaryData, buildBuildUpSummaryData, type FoulSummaryRow } from "../utils/foulSummaryUtils";
import { buildStormSummaryData, buildStormBuildUpSummaryData, type StormSummaryRow } from "../utils/stormSummaryUtils";
import { PUMP_STATION_ROWS, RISING_MAIN_ROWS } from "../utils/foulBuildUpRows";
import { getPondsRows, DEFAULT_POND_SECTIONS } from "../utils/stormBuildUpPonds";
import { getSwalesRows, DEFAULT_SWALE_SECTIONS } from "../utils/stormBuildUpSwales";
import { HEADWALL_ROWS } from "../utils/stormBuildUpHeadwalls";
import { getCellularAttenuationRows, DEFAULT_CELLULAR_SECTIONS } from "../utils/stormBuildUpCellularAttenuation";
import { getWeholiteAttenuationRows, DEFAULT_WEHOLITE_SECTIONS } from "../utils/stormBuildUpWeholiteAttenuation";
import { getConcAttenuationRows, DEFAULT_CONC_SECTIONS } from "../utils/stormBuildUpConcAttenuation";
import { getBoxCulvertsRows, DEFAULT_BOX_CULVERT_SECTIONS } from "../utils/stormBuildUpBoxCulverts";
import { findPondsSwalesEvaluateCode, findHeadwallEvaluateCode } from "../utils/stormPondsSwalesEvaluateCodesMapping";
import { findCellularAttenuationEvaluateCode } from "../utils/stormCellularAttenuationEvaluateCodesMapping";
import { findWeholiteAttenuationEvaluateCode } from "../utils/stormWeholiteAttenuationEvaluateCodesMapping";
import { resolveConcAttenuationEvaluateCode } from "../utils/stormConcAttenuationEvaluateCodesMapping";
import { findBoxCulvertsEvaluateCode } from "../utils/stormBoxCulvertsEvaluateCodesMapping";
import type { StormBuildUpRowDef } from "../utils/stormBuildUpTypes";
import { buildRoadsBillSummarySections, sumRoadsBillSummaryAmount } from "../utils/roadsSummaryCalculations";
import { sumMainServicesAmount } from "../utils/mainServiceTrenchingMapping";
import { sumSubstationAmount } from "../utils/substationBaseRowDefs";
import { sumPosAmount, DEFAULT_POS_SECTIONS } from "../utils/posRowDefs";
import { sumCompoundAmount } from "../utils/compoundRowDefs";
import type { CompoundConfig } from "../types/tender.types";
import { computeDynamicBillSummaryNodeAmount } from "../utils/roadsAndSewersDynamicBillSummarySerialization";
import { useRoadsAndSewersMarkupFactor } from "./useRoadsAndSewersMarkupFactor";

const readDisplayFlag = (key: string): boolean => {
  const saved = localStorage.getItem(key);
  return saved !== null ? (JSON.parse(saved) as boolean) : false;
};

export interface RoadsAndSewersSumAnalysis {
  foulTotal: number;
  stormTotal: number;
  roadsTotal: number;
  mainServicesTotal: number;
  substationTotal: number;
  posTotal: number;
  compoundTotal: number;
  prelimsTotal: number;
  pumpStationTotal: number;
  risingMainTotal: number;
  pondsSwalesTotal: number;
  cellularAttenuationTotal: number;
  weholiteAttenuationTotal: number;
  concAttenuationTotal: number;
  boxCulvertsTotal: number;
  /** Per-tab breakdown of Roads & Sewers Dynamic Bill Summary tabs
   *  (`state.roadsAndSewersDynamicBillSummaryTabs` — see useDynamicBillSummary.tsx /
   *  ReviewCalculate's "+ Add Tab" control), marked up the same way prelimsTotal is.
   *  Each entry rolls into `dynamicBillSummaryTotal`/`grandTotal` below. */
  dynamicBillSummaryTabTotals: { id: string; label: string; amount: number }[];
  dynamicBillSummaryTotal: number;
  /** Ad-hoc "Add Section"/"Add Item" extra rows (useBillSummaryExtraItems.tsx,
   *  reused from groundworks as-is) per fixed R&S Bill Summary — one combined
   *  total per domain across every attach-point bucket, marked up the same way
   *  prelimsTotal is. Exposed separately from e.g. prelimsTotal (rather than
   *  folded in here) purely so a caller CAN show a domain/extra-items split if
   *  it ever needs to — but every current call site folds each of these back
   *  into its matching domain total (`prelimsTotal + prelimsExtraItemsTotal`,
   *  etc.) before displaying a row, same convention groundworks' own
   *  SumAnalysis.tsx actually follows for cutFillsExtraItems etc. (one row per
   *  domain, extras folded in — never a separate "(Extra Items)" row; see
   *  `RsSumAnalysisBillSummary.tsx`'s `sections` and `ReviewCalculate.tsx`'s
   *  `sumAnalysisSections` export list). Each still rolls into `grandTotal`
   *  below regardless of how a caller displays it. */
  prelimsExtraItemsTotal: number;
  foulSewersExtraItemsTotal: number;
  stormSewersExtraItemsTotal: number;
  roadsExtraItemsTotal: number;
  mainServicesExtraItemsTotal: number;
  substationExtraItemsTotal: number;
  posExtraItemsTotal: number;
  compoundExtraItemsTotal: number;
  /** Same convention as the extra-items totals above, for the five build-up
   *  sub-tabs (Pump Station/Rising Main under Foul Sewers; Ponds & Swales/
   *  Cellular/Weholite/Conc Attenuation/Box Culverts under Storm Sewers) that
   *  gained their own "Add Item" support alongside their parent domain. */
  pumpStationExtraItemsTotal: number;
  risingMainExtraItemsTotal: number;
  pondsSwalesExtraItemsTotal: number;
  cellularAttenuationExtraItemsTotal: number;
  weholiteAttenuationExtraItemsTotal: number;
  concAttenuationExtraItemsTotal: number;
  boxCulvertsExtraItemsTotal: number;
  grandTotal: number;
  discountAmount: number;
  finalTotal: number;
}

export interface RoadsAndSewersTotalSyncOptions {
  includeOhpDisplay?: boolean;
  includePreliminariesDisplay?: boolean;
  includeDiscountDisplay?: boolean;
  /** Bump to force a prelims recompute even when memoized deps haven't structurally changed. */
  recalculateTrigger?: number;
}

/**
 * Computes the Roads & Sewers Sum Analysis TENDER TOTAL (Foul + Storm + Preliminary
 * Items, minus discount) — the same formula `RsSumAnalysisBillSummary.tsx` displays —
 * and keeps `tender.roadsAndSewersTotals` in sync with it.
 *
 * Call this from any mounted component to get live totals; call it (with no
 * `options` overrides, so it reads the same display-toggle flags from localStorage)
 * from a stable, always-mounted place (MainLayout.tsx) so the persisted total — used
 * for `billOfQuantities.roadAndSewers.totals.grandTotal` / `totals.tenderTotal` on
 * every save — never goes stale just because the user is working on a page other
 * than the Sum Analysis tab when they save.
 */
export const useRoadsAndSewersTotalSync = (
  itemLibraryData: ItemLibraryResponse | undefined,
  isItemLibraryLoading: boolean,
  options?: RoadsAndSewersTotalSyncOptions,
): RoadsAndSewersSumAnalysis => {
  const { state, updateField } = useTender();

  const includeOhpDisplay =
    options?.includeOhpDisplay ?? readDisplayFlag("roadsAndSewers_includeOhpDisplay");
  const includePreliminariesDisplay =
    options?.includePreliminariesDisplay ?? readDisplayFlag("roadsAndSewers_includePreliminariesDisplay");
  const includeDiscountDisplay =
    options?.includeDiscountDisplay ?? readDisplayFlag("roadsAndSewers_includeDiscountDisplay");

  // Shared OH&P/Preliminaries markup factor — every R&S domain total below
  // applies this to its own rows (matching each Bill Summary tab's own
  // marked-up Rate column), not just Preliminaries.
  const roadsAndSewersMarkupFactor = useRoadsAndSewersMarkupFactor({
    includeOhpDisplay,
    includePreliminariesDisplay,
  });

  // Raw (unrounded) nett rate per code — every "exact-match" R&S Bill Summary
  // (Foul/Storm/Substation/POS/Compound) computes its own displayed Rate as
  // `(nettRate * markupFactor).toFixed(2)`, rounding ONCE after markup. This
  // map deliberately stays unrounded so `markedUpRateFor` below can reproduce
  // that exact formula/order — rounding nettRate to 2dp here first (as this
  // map previously did) and marking that already-rounded value up afterward
  // is a double rounding that can land a penny away from what those Bill
  // Summary tabs actually display for the same row.
  const rateMap = useMemo(() => {
    const map: Record<string, number> = {};
    itemLibraryData?.forEach((item) => {
      if (item.resourceCode) map[item.resourceCode] = item.nettRate || 0;
    });
    return map;
  }, [itemLibraryData]);

  // Rate is rounded to 2dp here, post-markup — the SAME value every R&S Bill
  // Summary's own Rate column shows — so these totals match the sum of
  // what's actually displayed per row.
  const markedUpRateFor = (code: string): number =>
    parseFloat(((rateMap[code] ?? 0) * roadsAndSewersMarkupFactor).toFixed(2));

  const resourceCodeMap = useMemo(() => {
    const map = new Map<string, ItemLibraryItem>();
    itemLibraryData?.forEach((item) => {
      const code = item.resourceCode?.trim().toUpperCase();
      if (code) map.set(code, item);
    });
    return map;
  }, [itemLibraryData]);

  // A few Pump Station/Rising Main build-up evaluate codes are combined (e.g.
  // "50mm Gravel & Geotextile Areas" → "ALP050+ALP031") — sum whichever parts
  // resolve to a library rate, same convention as Storm's `getStormLibRate`
  // below (and `FoulBuildUpBillSummary.tsx`'s own `getLibRate`). Previously
  // missing here, so a combined-code row read as unpriced (£0) in this total.
  // Sums the already-marked-up, 2dp-rounded rate of each part (via
  // `markedUpRateFor`) rather than summing nett parts and marking up the
  // total afterward — `FoulBuildUpBillSummary.tsx`'s own `getLibRate` sums its
  // (already marked-up+rounded) per-code `rateMap` the same way, and the two
  // orders can round to a different penny.
  const getFoulLibRate = (code?: string): number => {
    if (!code) return 0;
    if (code.includes("+")) {
      return code.split("+").reduce((sum, c) => sum + markedUpRateFor(c.trim()), 0);
    }
    return markedUpRateFor(code.trim());
  };

  // No further rounding here — `getFoulLibRate` already returns the final,
  // marked-up-and-rounded per-row rate (see above), matching what every R&S
  // Bill Summary's own Rate column shows.
  const sumFoulRows = (rows: FoulSummaryRow[]): number =>
    rows.reduce((acc, row) => {
      if (row.children) return acc + sumFoulRows(row.children);
      if (row.rowType !== "item") return acc;
      const qty = parseFloat(row.quantity ?? "");
      const rate = row.evaluateCode ? getFoulLibRate(row.evaluateCode) : 0;
      return isNaN(qty) ? acc : acc + qty * rate;
    }, 0);

  // A few Storm build-up evaluate codes are combined (e.g. "ALPE010+L001B") — sum
  // whichever parts resolve to a library rate, same convention as
  // StormBuildUpTable.tsx's/StormBuildUpBillSummary.tsx's own getLibRate. Same
  // sum-of-rounded-parts order as getFoulLibRate above, for the same reason.
  const getStormLibRate = (code?: string): number => {
    if (!code) return 0;
    if (code.includes("+")) {
      return code.split("+").reduce((sum, c) => sum + markedUpRateFor(c.trim()), 0);
    }
    return markedUpRateFor(code.trim());
  };

  const sumStormRows = (rows: StormSummaryRow[]): number =>
    rows.reduce((acc, row) => {
      if (row.children) return acc + sumStormRows(row.children);
      if (row.rowType !== "item") return acc;
      const qty = parseFloat(row.quantity ?? "");
      const rate = row.evaluateCode ? getStormLibRate(row.evaluateCode) : 0;
      return isNaN(qty) ? acc : acc + qty * rate;
    }, 0);

  const foulTotal = useMemo(() => {
    const foul = state.foulSewers;
    if (!foul) return 0;
    const rows = buildFoulSummaryData(
      foul.pipeRuns.table || [],
      foul.manholes.table || [],
      foul.lateralConnections.table || [],
      foul.pipeRuns.config.eoQtys,
      foul.manholes.config.manholeScheduleConfig,
    );
    return sumFoulRows(rows);
  }, [state.foulSewers, rateMap, roadsAndSewersMarkupFactor]);

  const stormTotal = useMemo(() => {
    const storm = state.stormSewers;
    if (!storm) return 0;
    const rows = buildStormSummaryData(
      storm.pipeRuns.table || [],
      storm.manholes.table || [],
      storm.lateralConnections.table || [],
      storm.gullyConnections.table || [],
      storm.pipeRuns.config.eoQtys,
      storm.manholes.config.manholeScheduleConfig,
    );
    return sumStormRows(rows);
  }, [state.stormSewers, rateMap, roadsAndSewersMarkupFactor]);

  // Pump Station / Rising Main (Foul) and Ponds & Swales / Cellular / Weholite /
  // Conc Attenuation / Box Culverts (Storm) build-up totals — previously excluded
  // from foulTotal/stormTotal entirely (those only summed the main Drainage
  // Runs/Manholes-style rows), so the Sum Analysis TENDER TOTAL silently omitted
  // any quantity entered on these build-up tabs. Same evaluate-code resolution
  // `FoulSewersBillSummary.tsx`/`StormSewersBillSummary.tsx` use for their own
  // Bill Summary tabs.
  const pumpChamberConfig = (state.pumpStation?.config as PumpChamberConfig)?.coverLevel !== undefined
    ? (state.pumpStation?.config as PumpChamberConfig)
    : undefined;

  const pumpStationTotal = useMemo(
    () => sumFoulRows(buildBuildUpSummaryData(PUMP_STATION_ROWS, state.pumpStation?.table, pumpChamberConfig)),
    [state.pumpStation?.table, pumpChamberConfig, rateMap, roadsAndSewersMarkupFactor],
  );
  const risingMainTotal = useMemo(
    () => sumFoulRows(buildBuildUpSummaryData(RISING_MAIN_ROWS, state.risingMain?.table)),
    [state.risingMain?.table, rateMap, roadsAndSewersMarkupFactor],
  );

  const pondSectionCount = state.pondsSwales?.config?.pondSectionCount ?? DEFAULT_POND_SECTIONS;
  const exTopsoilMm = Math.round((state.roads?.cutFillSections?.config?.stripValueDefault ?? 0.3) * 1000);
  const pondTopsoilThicknessMm = state.pondsSwales?.config?.pondTopsoilThicknessMm ?? {};
  const swaleSectionCount = state.pondsSwales?.config?.swaleSectionCount ?? DEFAULT_SWALE_SECTIONS;
  const swaleTopsoilThicknessMm = state.pondsSwales?.config?.swaleTopsoilThicknessMm ?? {};
  const resolvePondsSwalesEvaluateCode = (row: StormBuildUpRowDef): string =>
    row.id.startsWith("hw_")
      ? findHeadwallEvaluateCode(row.description) ?? ""
      : findPondsSwalesEvaluateCode(row.description) ?? "";
  const pondsSwalesTotal = useMemo(() => {
    const rows: StormBuildUpRowDef[] = [
      ...getPondsRows(pondSectionCount, exTopsoilMm, pondTopsoilThicknessMm),
      ...getSwalesRows(swaleSectionCount, exTopsoilMm, swaleTopsoilThicknessMm),
      ...HEADWALL_ROWS,
    ];
    return sumStormRows(buildStormBuildUpSummaryData(rows, state.pondsSwales?.table, resolvePondsSwalesEvaluateCode));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pondSectionCount, exTopsoilMm, pondTopsoilThicknessMm, swaleSectionCount, swaleTopsoilThicknessMm, state.pondsSwales?.table, rateMap, roadsAndSewersMarkupFactor]);

  const cellularSectionCount = state.cellularAttenuation?.config?.sectionCount ?? DEFAULT_CELLULAR_SECTIONS;
  const cellularCrateDims = state.cellularAttenuation?.config?.crateDims;
  const cellularAttenuationTotal = useMemo(() => {
    const rows = getCellularAttenuationRows(cellularSectionCount, undefined, cellularCrateDims);
    return sumStormRows(buildStormBuildUpSummaryData(
      rows, state.cellularAttenuation?.table, (row) => findCellularAttenuationEvaluateCode(row.description) ?? "",
    ));
  }, [cellularSectionCount, cellularCrateDims, state.cellularAttenuation?.table, rateMap, roadsAndSewersMarkupFactor]);

  const weholiteSectionCount = state.weholiteAttenuation?.config?.sectionCount ?? DEFAULT_WEHOLITE_SECTIONS;
  const weholiteSupplierName = state.weholiteAttenuation?.config?.supplierName ?? "Weholite";
  const weholiteConfigPct = state.weholiteAttenuation?.config?.weholiteConfigPct ?? {};
  const weholiteAttenuationTotal = useMemo(() => {
    const rows = getWeholiteAttenuationRows(weholiteSectionCount, weholiteConfigPct, weholiteSupplierName);
    return sumStormRows(buildStormBuildUpSummaryData(
      rows, state.weholiteAttenuation?.table, (row) => findWeholiteAttenuationEvaluateCode(row.id) ?? "",
    ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weholiteSectionCount, weholiteConfigPct, weholiteSupplierName, state.weholiteAttenuation?.table, rateMap, roadsAndSewersMarkupFactor]);

  const concSectionCount = state.concAttenuation?.config?.sectionCount ?? DEFAULT_CONC_SECTIONS;
  const concConfigPct = state.concAttenuation?.config?.concConfigPct ?? {};
  const concSupplierName = state.concAttenuation?.config?.supplierName ?? "Carlow";
  const concAttenuationTotal = useMemo(() => {
    const rows = getConcAttenuationRows(concSectionCount, concConfigPct, concSupplierName);
    return sumStormRows(buildStormBuildUpSummaryData(
      rows, state.concAttenuation?.table, (row) => resolveConcAttenuationEvaluateCode(row) ?? "",
    ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [concSectionCount, concConfigPct, concSupplierName, state.concAttenuation?.table, rateMap, roadsAndSewersMarkupFactor]);

  const boxCulvertSectionCount = state.boxCulverts?.config?.sectionCount ?? DEFAULT_BOX_CULVERT_SECTIONS;
  const culvertConfigMm = state.boxCulverts?.config?.culvertConfigMm ?? {};
  const culvertBackfillType = state.boxCulverts?.config?.culvertBackfillType ?? {};
  const boxCulvertsTotal = useMemo(() => {
    const rows = getBoxCulvertsRows(boxCulvertSectionCount, culvertConfigMm, culvertBackfillType);
    return sumStormRows(buildStormBuildUpSummaryData(
      rows, state.boxCulverts?.table, (row) => findBoxCulvertsEvaluateCode(row.description) ?? "",
    ));
  }, [boxCulvertSectionCount, culvertConfigMm, culvertBackfillType, state.boxCulverts?.table, rateMap, roadsAndSewersMarkupFactor]);

  // Roads and Main Services are the two domains whose own Bill Summary
  // (`RoadsBillSummary.tsx`'s `resourceCodeMap`, `MainServicesBillSummary.tsx`'s
  // `rateMap`) both (a) normalizes both sides of its item-library lookup
  // (trim+uppercase), unlike every other domain here (Foul/Storm/Substation/POS/
  // Compound, all exact-match only — see those bills' own `getLibRate`/`rateMap`),
  // and (b) falls back to `baseRate` when `nettRate` is 0/unset, unlike the plain
  // `rateMap` above (nettRate only, matching every other domain's own Bill
  // Summary). Built directly from `itemLibraryData` — not derived from `rateMap`
  // — for both reasons: reusing `rateMap` would carry over its missing baseRate
  // fallback (silently pricing any nettRate-less code at £0 here while Roads/Main
  // Services' own Bill Summary shows its baseRate), and it kept the raw rate
  // unrounded so markup is applied once, the same order RoadsBillSummary.tsx/
  // MainServicesBillSummary.tsx compute it (`(nettRate||baseRate||0) *
  // markupFactor).toFixed(2)`) instead of rounding the nett rate first and the
  // marked-up rate again after, which could round differently by a penny.
  const normalizedRateMap = useMemo(() => {
    const map: Record<string, number> = {};
    itemLibraryData?.forEach((item) => {
      const code = item.resourceCode?.trim().toUpperCase();
      if (code) map[code] = item.nettRate || item.baseRate || 0;
    });
    return map;
  }, [itemLibraryData]);
  // A few Roads evaluate codes are combined too (e.g. "Tree pits" →
  // "ALP200+ALP201") — same "+"-combined convention as getFoulLibRate/
  // getStormLibRate above; sum whichever parts resolve. Previously missing
  // here, so a combined-code Roads row read as unpriced (£0) in this total.
  const normalizedMarkedUpRateFor = (code: string): number => {
    const trimmed = code.trim();
    if (trimmed.includes("+")) {
      return trimmed.split("+").reduce((sum, c) => sum + normalizedMarkedUpRateFor(c.trim()), 0);
    }
    return parseFloat(((normalizedRateMap[trimmed.toUpperCase()] ?? 0) * roadsAndSewersMarkupFactor).toFixed(2));
  };

  // Carriageway (Roads) Bill Summary — same section tree `RoadsBillSummary.tsx` renders,
  // so this can never drift from what that page shows as its own TOTAL CARRIAGEWAY row.
  const roadsTotal = useMemo(
    () => sumRoadsBillSummaryAmount(buildRoadsBillSummarySections(state.roads), normalizedMarkedUpRateFor),
    [state.roads, normalizedRateMap, roadsAndSewersMarkupFactor],
  );

  // Main Services Bill Summary — same `MAIN_SERVICE_TRENCHING_MAPPING`/evaluate-code
  // resolution `MainServicesBillSummary.tsx` sums for its own TOTAL MAIN SERVICES row.
  const mainServicesTotal = useMemo(
    () => sumMainServicesAmount(state.mainServices?.table ?? {}, normalizedMarkedUpRateFor),
    [state.mainServices?.table, normalizedRateMap, roadsAndSewersMarkupFactor],
  );

  // Substation Bill Summary — same live description-based evaluate code resolution
  // `SubstationBillSummary.tsx` uses for its own TOTAL SUBSTATION row (never a persisted
  // evaluateCode, which `MainServices.tsx`'s `updateSubRow` never actually writes).
  const substationTotal = useMemo(
    () => sumSubstationAmount(state.substation?.table ?? {}, markedUpRateFor),
    [state.substation?.table, rateMap, roadsAndSewersMarkupFactor],
  );

  // POS (Public Open Space) Bill Summary — same live configured-description evaluate
  // code resolution `POSBillSummary.tsx` uses for its own TOTAL POS row.
  const posSectionCount = state.pos?.config?.sectionCount ?? DEFAULT_POS_SECTIONS;
  const posRowThicknessMm = state.pos?.config?.rowThicknessMm ?? {};
  const posRowText = state.pos?.config?.rowText ?? {};
  const posGeotextileIncluded = state.pos?.config?.geotextileIncluded ?? {};
  const posTotal = useMemo(
    () => sumPosAmount(state.pos?.table ?? {}, posSectionCount, posRowThicknessMm, posRowText, markedUpRateFor, posGeotextileIncluded),
    [state.pos?.table, posSectionCount, posRowThicknessMm, posRowText, rateMap, roadsAndSewersMarkupFactor],
  );

  // Compound Bill Summary — same `COMPOUND_ROW_DEFS`/evaluate-code resolution
  // `CompoundBillSummary.tsx` sums for its own TOTAL COMPOUND row. Previously
  // missing from this hook entirely (only `compoundExtraItemsTotal` existed),
  // so Compound's own fixed items never reached the Sum Analysis grand total
  // or the persisted `roadsAndSewersTotals.grandTotal` — fixed here since
  // marking Compound up without including it in the grand total would be
  // invisible to Sum Analysis.
  const compoundTotal = useMemo(
    () => sumCompoundAmount(state.compound?.table ?? {}, (state.compound?.config ?? {}) as CompoundConfig, markedUpRateFor),
    [state.compound?.table, state.compound?.config, rateMap, roadsAndSewersMarkupFactor],
  );

  const roadsAndSewersSettings = (state.globalSettings as unknown as Record<string, Record<string, number>>)
    ?.roadsAndSewers;

  // Roads & Sewers Dynamic Bill Summary tabs (user-added "+ Add Tab" sections on
  // /roads-sewers/review's "Bill of Quantities Summary" tab bar) — same reusable
  // useDynamicBillSummary.tsx feature Groundworks Review uses, minus Plot Matrix
  // mapping (Roads & Sewers has Run Matrix instead, a different per-section concept).
  const dynamicBillSummaryTabTotals = useMemo(
    () =>
      (state.roadsAndSewersDynamicBillSummaryTabs ?? []).map((tab) => ({
        id: tab.id,
        label: tab.label,
        amount: tab.nodes.reduce(
          (sum, node) => sum + computeDynamicBillSummaryNodeAmount(node, roadsAndSewersMarkupFactor),
          0,
        ),
      })),
    [state.roadsAndSewersDynamicBillSummaryTabs, roadsAndSewersMarkupFactor],
  );
  const dynamicBillSummaryTotal = useMemo(
    () => dynamicBillSummaryTabTotals.reduce((sum, tab) => sum + tab.amount, 0),
    [dynamicBillSummaryTabTotals],
  );

  // Ad-hoc "Add Section"/"Add Item" extra rows (useBillSummaryExtraItems.tsx)
  // per fixed R&S Bill Summary — same computeDynamicBillSummaryNodeAmount
  // summing dynamicBillSummaryTabTotals above uses, walked across every
  // attach-point bucket instead of one flat node array. Every domain now
  // applies the same shared roadsAndSewersMarkupFactor its own fixed rows do
  // — extra items on any R&S Bill Summary are marked up exactly like every
  // real row on that same tab.
  const sumExtraItemsBuckets = (buckets: Record<string, DynamicBillSummaryNode[]> | undefined, markupFactor = 1): number =>
    Object.values(buckets ?? {}).reduce(
      (sum, nodes) => sum + nodes.reduce((s, node) => s + computeDynamicBillSummaryNodeAmount(node, markupFactor), 0),
      0,
    );
  const prelimsExtraItemsTotal = useMemo(
    () => sumExtraItemsBuckets(state.prelimsExtraItems, roadsAndSewersMarkupFactor),
    [state.prelimsExtraItems, roadsAndSewersMarkupFactor],
  );
  const foulSewersExtraItemsTotal = useMemo(
    () => sumExtraItemsBuckets(state.foulSewersExtraItems, roadsAndSewersMarkupFactor),
    [state.foulSewersExtraItems, roadsAndSewersMarkupFactor],
  );
  const stormSewersExtraItemsTotal = useMemo(
    () => sumExtraItemsBuckets(state.stormSewersExtraItems, roadsAndSewersMarkupFactor),
    [state.stormSewersExtraItems, roadsAndSewersMarkupFactor],
  );
  const roadsExtraItemsTotal = useMemo(
    () => sumExtraItemsBuckets(state.roadsExtraItems, roadsAndSewersMarkupFactor),
    [state.roadsExtraItems, roadsAndSewersMarkupFactor],
  );
  const mainServicesExtraItemsTotal = useMemo(
    () => sumExtraItemsBuckets(state.mainServicesExtraItems, roadsAndSewersMarkupFactor),
    [state.mainServicesExtraItems, roadsAndSewersMarkupFactor],
  );
  const substationExtraItemsTotal = useMemo(
    () => sumExtraItemsBuckets(state.substationExtraItems, roadsAndSewersMarkupFactor),
    [state.substationExtraItems, roadsAndSewersMarkupFactor],
  );
  const posExtraItemsTotal = useMemo(
    () => sumExtraItemsBuckets(state.posExtraItems, roadsAndSewersMarkupFactor),
    [state.posExtraItems, roadsAndSewersMarkupFactor],
  );
  const compoundExtraItemsTotal = useMemo(
    () => sumExtraItemsBuckets(state.compoundExtraItems, roadsAndSewersMarkupFactor),
    [state.compoundExtraItems, roadsAndSewersMarkupFactor],
  );
  const pumpStationExtraItemsTotal = useMemo(
    () => sumExtraItemsBuckets(state.pumpStationExtraItems, roadsAndSewersMarkupFactor),
    [state.pumpStationExtraItems, roadsAndSewersMarkupFactor],
  );
  const risingMainExtraItemsTotal = useMemo(
    () => sumExtraItemsBuckets(state.risingMainExtraItems, roadsAndSewersMarkupFactor),
    [state.risingMainExtraItems, roadsAndSewersMarkupFactor],
  );
  const pondsSwalesExtraItemsTotal = useMemo(
    () => sumExtraItemsBuckets(state.pondsSwalesExtraItems, roadsAndSewersMarkupFactor),
    [state.pondsSwalesExtraItems, roadsAndSewersMarkupFactor],
  );
  const cellularAttenuationExtraItemsTotal = useMemo(
    () => sumExtraItemsBuckets(state.cellularAttenuationExtraItems, roadsAndSewersMarkupFactor),
    [state.cellularAttenuationExtraItems, roadsAndSewersMarkupFactor],
  );
  const weholiteAttenuationExtraItemsTotal = useMemo(
    () => sumExtraItemsBuckets(state.weholiteAttenuationExtraItems, roadsAndSewersMarkupFactor),
    [state.weholiteAttenuationExtraItems, roadsAndSewersMarkupFactor],
  );
  const concAttenuationExtraItemsTotal = useMemo(
    () => sumExtraItemsBuckets(state.concAttenuationExtraItems, roadsAndSewersMarkupFactor),
    [state.concAttenuationExtraItems, roadsAndSewersMarkupFactor],
  );
  const boxCulvertsExtraItemsTotal = useMemo(
    () => sumExtraItemsBuckets(state.boxCulvertsExtraItems, roadsAndSewersMarkupFactor),
    [state.boxCulvertsExtraItems, roadsAndSewersMarkupFactor],
  );

  // Delegates to the shared `sumPrelimsAmount` (prelimsMapping.ts) rather than
  // reimplementing this walk inline — that util already does the variant-aware
  // code resolution (e.g. "CBR testing option(s)") and legacy-group-id saved-row
  // lookup PrelimsBillSummary.tsx's own walk does; a separate inline copy here
  // previously used the item's default code even when the user had selected a
  // variant, silently pricing that row differently from what the Bill Summary
  // itself displays for it.
  const prelimsTotal = useMemo(
    () => sumPrelimsAmount(
      state.preliminaryItems?.preliminaryItems,
      (code) => {
        const libItem = resourceCodeMap.get(code);
        return libItem ? libItem.nettRate || libItem.baseRate || 0 : 0;
      },
      roadsAndSewersMarkupFactor,
    ),
    [
      state.preliminaryItems?.preliminaryItems,
      resourceCodeMap,
      roadsAndSewersMarkupFactor,
      options?.recalculateTrigger,
    ],
  );

  const grandTotal = foulTotal + stormTotal + roadsTotal + mainServicesTotal + substationTotal + posTotal + compoundTotal + prelimsTotal
    + pumpStationTotal + risingMainTotal
    + pondsSwalesTotal + cellularAttenuationTotal + weholiteAttenuationTotal + concAttenuationTotal + boxCulvertsTotal
    + dynamicBillSummaryTotal
    + prelimsExtraItemsTotal + foulSewersExtraItemsTotal + stormSewersExtraItemsTotal + roadsExtraItemsTotal
    + mainServicesExtraItemsTotal + substationExtraItemsTotal + posExtraItemsTotal + compoundExtraItemsTotal
    + pumpStationExtraItemsTotal + risingMainExtraItemsTotal
    + pondsSwalesExtraItemsTotal + cellularAttenuationExtraItemsTotal + weholiteAttenuationExtraItemsTotal
    + concAttenuationExtraItemsTotal + boxCulvertsExtraItemsTotal;

  const discountPercent = roadsAndSewersSettings?.discount ?? 0;
  const discountAmount =
    includeDiscountDisplay && discountPercent > 0 ? grandTotal * (discountPercent / 100) : 0;
  const finalTotal = grandTotal - discountAmount;

  // Keep tender.roadsAndSewersTotals in sync — gated on rates being loaded (avoids
  // baking in a 0/incomplete total computed against an empty rateMap) and on the
  // R&S job type actually being selected.
  useEffect(() => {
    if (!state.jobTypes?.roadsAndSewers) return;
    if (isItemLibraryLoading) return;
    // On /groundworks the R&S inputs aren't hydrated/edited, so a live recompute
    // would clobber the persisted R&S total (hydrated from the saved BOQ) with a
    // partial/0 value — keep the stored figure so grand totals still include it.
    if (getBoqVersionRoutePrefix() === "GWKS") return;
    const stored = state.roadsAndSewersTotals?.grandTotal;
    if (stored === finalTotal) return;
    updateField("roadsAndSewersTotals", {
      grandTotal: finalTotal,
      calculatedAt: new Date().toISOString(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalTotal, isItemLibraryLoading, state.jobTypes?.roadsAndSewers]);

  return {
    foulTotal, stormTotal, roadsTotal, mainServicesTotal, substationTotal, posTotal, compoundTotal, prelimsTotal,
    pumpStationTotal, risingMainTotal,
    pondsSwalesTotal, cellularAttenuationTotal, weholiteAttenuationTotal, concAttenuationTotal, boxCulvertsTotal,
    dynamicBillSummaryTabTotals, dynamicBillSummaryTotal,
    prelimsExtraItemsTotal, foulSewersExtraItemsTotal, stormSewersExtraItemsTotal, roadsExtraItemsTotal,
    mainServicesExtraItemsTotal, substationExtraItemsTotal, posExtraItemsTotal, compoundExtraItemsTotal,
    pumpStationExtraItemsTotal, risingMainExtraItemsTotal,
    pondsSwalesExtraItemsTotal, cellularAttenuationExtraItemsTotal, weholiteAttenuationExtraItemsTotal,
    concAttenuationExtraItemsTotal, boxCulvertsExtraItemsTotal,
    grandTotal, discountAmount, finalTotal,
  };
};
